import _ from "lodash";
/** external */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AddOutlined } from "@toddle-edu/ds-icons";
import { TextInput, IconButton, Tooltip } from "@toddle-edu/ds-web";
import update from "immutability-helper";

/** internal */
import { useI18n } from "Hooks";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import {
  checkQuestionNumberValidity,
  getRandomQuestionLibraryFeedQueryVariables,
  getLibraryQuestionCardDetails,
  getLibraryQuestionCardDetailsV2,
  getSchoolQuestionLibraryFeedQueryVariables,
  getToddleQuestionLibraryFeedQueryVariables,
} from "Worksheet/modules/WorksheetUtils";
import { getIsToddleLibraryWorksheetQueriesV2Enabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { TODDLE_LIBRARY as ACTIVE_TAB_TODDLE_LIBRARY } from "Constants/stringConstants";
import {
  updateSelectedLibraryQuestions,
  fetchRandomQuestions,
  updateLoadingState,
} from "Worksheet/modules/WorksheetModules";
import {
  SCHOOL_LIBRARY,
  TODDLE_LIBRARY,
  GRADE_11_and_12,
  QUESTION_IMPORT_LIMIT,
} from "Worksheet/modules/WorksheetConstants";
import { getSelectedCurriculumId } from "modules/CommonSelectors";
import { getLibraryGradesFromCache } from "ToddleLibrary/modules/ToddleLibraryGraphqlHelpers";

/** relative */
import classes from "./AddRandomQuestions.scss";
import ConditionalWrapper from "Utils/ConditionalWrapper";

const AddRandomQuestions = props => {
  const {
    questionLimit,
    selectedQuestions,
    updateSelectedLibraryQuestions,
    updateLoadingState,
    questionLibraryFeedVariables,
    libraryType,
    fetchRandomQuestions,
    setToastMsg,
  } = props;

  const { t } = useI18n(["common", "worksheet"]);
  const [numberOfQuestions, setNumberOfQuestions] = useState(null);

  const isInputDisabled = _.size(selectedQuestions) >= QUESTION_IMPORT_LIMIT;
  const onTextInputChange = e => {
    const value = _.get(e, "target.value");
    const validInput = checkQuestionNumberValidity({
      numberOfQuestions: value,
      totalNumberOfQuestions: questionLimit,
    });

    if (validInput) {
      setNumberOfQuestions(value);
    } else {
      setNumberOfQuestions(numberOfQuestions);
      e.target.value = numberOfQuestions;
    }
  };

  const getRandomQuestions = async () => {
    const randomQuestionLibraryFeedVariables = getRandomQuestionLibraryFeedQueryVariables(
      {
        questionLibraryFeedVariables,
        numberOfQuestions,
        libraryType,
        selectedQuestions,
      }
    );

    if (libraryType === SCHOOL_LIBRARY) {
      _.set(
        randomQuestionLibraryFeedVariables,
        "orgResourceFilters.questionFilters",
        randomQuestionLibraryFeedVariables?.questionFilters
      );
    }

    updateLoadingState(true);
    const data = await fetchRandomQuestions({
      variables: randomQuestionLibraryFeedVariables,
      libraryType,
    });
    const isToddleLibrary = libraryType === TODDLE_LIBRARY;
    const isToddleLibraryV2 =
      isToddleLibrary && getIsToddleLibraryWorksheetQueriesV2Enabled();
    const questions = isToddleLibrary
      ? _.get(data, ["libraryEntities", "edges"])
      : _.get(data, ["organizationResourcesV2", "edges"]);
    const newQuestions = [];
    _.forEach(questions, item => {
      const questionResource = _.get(item, "node", {});

      const libraryCardDetails = isToddleLibraryV2
        ? getLibraryQuestionCardDetailsV2({
            question: questionResource,
            t,
            isToddleLibrary,
          })
        : getLibraryQuestionCardDetails({
            questionResource,
            t,
          });
      if (!_.some(selectedQuestions, ["id", _.get(libraryCardDetails, "id")])) {
        newQuestions.push(libraryCardDetails);
      }
    });
    const finalDetails = [...selectedQuestions, ...newQuestions];
    updateSelectedLibraryQuestions(finalDetails);
    updateLoadingState(false);
    setToastMsg({
      msg: t("common:no_question_selected_plural", {
        count: numberOfQuestions,
      }),
      notString: true,
      type: "success",
      position: "toast-bottom-left",
    });
    setNumberOfQuestions(null);
  };

  useEffect(() => {
    setNumberOfQuestions(null);
  }, [selectedQuestions]);

  return (
    <div className={classes.container}>
      <h6 className="heading-6">{t("worksheet:add_random_questions")}</h6>
      <div className={classes.numberInput}>
        <ConditionalWrapper
          type={"wrapperAsElement"}
          condition={isInputDisabled}
          wrapper={
            <Tooltip
              tooltip={t(
                "worksheet:you_can_only_add_up_to_questions_at_a_time_with_count",
                { count: QUESTION_IMPORT_LIMIT }
              )}
              placement="bottom"
              overlayInnerStyle={{
                maxWidth: "136px",
                textAlign: "center",
              }}
            />
          }
        >
          <TextInput
            value={numberOfQuestions}
            onChange={onTextInputChange}
            disabled={isInputDisabled}
            placeholder="0"
            style={{
              paddingLeft: "16px",
              backgroundColor: "transparent",
            }}
            trailingIcon={
              <IconButton
                dsVersion="2.0"
                variant="progressive"
                size="small"
                _legacy_variant="progressive"
                _legacy_size="small"
                disabled={
                  isInputDisabled ||
                  _.isNil(numberOfQuestions) ||
                  numberOfQuestions == 0
                }
                onClick={getRandomQuestions}
                icon={<AddOutlined size="small" />}
              />
            }
          ></TextInput>
        </ConditionalWrapper>
      </div>
    </div>
  );
};

AddRandomQuestions.propTypes = {
  questionLimit: PropTypes.number,
  selectedQuestions: PropTypes.array,
  state: PropTypes.object,
  libraryUserInfo: PropTypes.object,
  isToddleLibraryPortal: PropTypes.bool,
  updateSelectedLibraryQuestions: PropTypes.func,
  updateLoadingState: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
  const { isSubQuestion } = ownProps;

  const libraryUserInfo = getUserInfo({ portalType: "COMMUNITY" });
  const libraryUserId = _.get(libraryUserInfo, "id", "");

  const libraryType = _.get(state, "worksheet.questionLibrary.libraryType", []);

  const isToddleLibraryPortal =
    state.login.activeTab === ACTIVE_TAB_TODDLE_LIBRARY;

  const userInfo = getUserInfo({ portalType: "PLANNER" });
  const isToddleLibrary = libraryType === TODDLE_LIBRARY;

  const organizationId = isToddleLibrary
    ? _.get(libraryUserInfo, "org_id", "")
    : _.get(userInfo, "org_id", "");

  const questionLibraryFilters = _.get(
    state,
    "worksheet.questionLibrary.filters",
    {}
  );

  const curriculumProgramId = getSelectedCurriculumId(state);

  const parentEntity = _.get(state, "worksheet.parentEntity", {});

  const questionLibraryFeedVariables = _.isEqual(libraryType, TODDLE_LIBRARY)
    ? getToddleQuestionLibraryFeedQueryVariables({
        userId: libraryUserId,
        parentEntity,
        questionLibraryFilters,
        isSubQuestion,
      })
    : getSchoolQuestionLibraryFeedQueryVariables({
        libraryType,
        organizationId,
        isSubQuestion,
        questionLibraryFilters,
        curriculumProgramId,
        parentEntity,
      });

  const organizationGradeDetails = getLibraryGradesFromCache({
    id: organizationId,
  });
  const dpGrade = _.find(
    _.get(organizationGradeDetails, ["node", "libraryGrades"], []),
    ["label", GRADE_11_and_12]
  );
  const dpGradeId = _.get(dpGrade, "id", "");

  const updatedFeedVariables =
    !_.isEmpty(dpGradeId) && isToddleLibrary
      ? update(questionLibraryFeedVariables, {
          filters: {
            grades: {
              $set: [dpGradeId],
            },
          },
        })
      : questionLibraryFeedVariables;

  return {
    questionLibraryFeedVariables: updatedFeedVariables,
    libraryType,
    state,
    libraryUserInfo,
    isToddleLibraryPortal,
  };
};
const mapActionCreators = {
  updateSelectedLibraryQuestions,
  updateLoadingState,
  fetchRandomQuestions,
  setToastMsg,
};

export default connect(mapStateToProps, mapActionCreators)(AddRandomQuestions);
